import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';

import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { PageHeader } from '@/components/shared/page-header';
import { StatCard } from '@/components/shared/stat-card';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { PLATFORM_AUDIT_ACTIONS } from '@/lib/platform-audit-actions';
import { api } from '@/lib/api';
import { formatDate, formatDateTime, formatMoney } from '@/utils/format';

interface DashboardChurchRef {
  churchId: string;
  churchName: string;
}

interface PlatformDashboard {
  generatedAt: string;
  attention: {
    overdue: Array<
      DashboardChurchRef & {
        status: 'past_due' | 'grace' | 'suspended';
        pastDueSince: string | null;
        readOnlyFrom: string | null;
      }
    >;
    overdueTotal: number;
    trialsEnding: Array<DashboardChurchRef & { trialEndsAt: string }>;
    trialsEndingTotal: number;
    paymentConnectionsNeedingAttention: number;
    giftsNeedingReview: number;
    paymentsSuspended: number;
    unresolvedFailedTasks: number;
    activeSupportVisits: number;
    supportTicketsAwaitingReply: number;
    supportTicketsEscalated: number;
    supportTicketsSlaBreached: number;
  };
  growth: {
    churches: { total: number; active: number; suspended: number; newInWindow: number };
    users: { total: number; active: number; newInWindow: number };
    signupsByWeek: Array<{ weekStart: string; churches: number }>;
    windowDays: number;
  };
  subscriptions: {
    byStatus: Record<
      'trialing' | 'active' | 'past_due' | 'grace' | 'suspended' | 'cancelled' | 'expired',
      number
    >;
    byPlan: Array<{ plan: string; churches: number }>;
    collected: Array<{ currency: string; amountMinor: number; payments: number }>;
    outstanding: Array<{ currency: string; amountMinor: number; invoices: number }>;
    windowDays: number;
  };
  adoption: {
    modules: Array<{ moduleCode: string; churches: number }>;
    onlineGivingConnected: number;
    consoleUsers: number;
    appMembers: number;
  };
  giving: {
    attempts: number;
    succeeded: number;
    failed: number;
    waiting: number;
    needsReview: number;
    successRate: number | null;
    windowDays: number;
  };
  messaging: {
    byChannel: Array<{
      channel: 'push' | 'sms' | 'email';
      total: number;
      delivered: number;
      failed: number;
    }>;
    failureRate: number | null;
    windowDays: number;
  };
  security: {
    platformStaffWithoutTwoStep: number;
    suspendedOrBannedUsers: number;
    stolenSignInsBlocked: number;
    recentCritical: Array<{
      id: string;
      action: string;
      summary: string;
      churchName: string | null;
      actorName: string | null;
      createdAt: string;
    }>;
    windowDays: number;
  };
  system: {
    database: 'ok' | 'unavailable';
    schedulerEnabled: boolean;
    tasksFailingNow: string[];
    tasksOverdue: string[];
    email: { configured: boolean };
    sms: { configured: boolean };
    uptimeSeconds: number;
  };
}

const SUBSCRIPTION_STATUSES = [
  'trialing',
  'active',
  'past_due',
  'grace',
  'suspended',
  'cancelled',
  'expired',
] as const;

const formatRate = (value: number | null, locale: string): string => {
  if (value === null) return '—';
  return new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 1 }).format(value);
};

const formatUptime = (seconds: number, t: (key: string, options?: Record<string, unknown>) => string): string => {
  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return t('operations.uptime.daysHours', { days, hours });
  if (hours > 0) return t('operations.uptime.hoursMinutes', { hours, minutes });
  return t('operations.uptime.minutes', { count: minutes });
};

const AttentionLink = ({
  to,
  count,
  children,
}: {
  to: string;
  count: number;
  children: string;
}) => (
  <Link
    to={to}
    className={`block rounded-md px-3 py-2 text-body-sm transition-colors hover:bg-surface-sunken ${
      count > 0 ? 'font-medium text-fg' : 'text-fg-secondary'
    }`}
  >
    {children}
  </Link>
);

export const DashboardPage = () => {
  const { t, i18n } = useTranslation('admin');
  const dashboard = useQuery({
    queryKey: ['platform-dashboard'],
    queryFn: () => api.get<PlatformDashboard>('/platform/dashboard'),
  });

  const data = dashboard.data;
  const locale = i18n.language;
  const jobLabel = (name: string) => t(`operations.jobNames.${name}`, { defaultValue: name });
  const moduleLabel = (code: string) => t(`dashboard.modules.${code}`, { defaultValue: code });
  const actionLabel = (action: string) =>
    (PLATFORM_AUDIT_ACTIONS as readonly string[]).includes(action)
      ? t(`audit.actions.${action as (typeof PLATFORM_AUDIT_ACTIONS)[number]}`)
      : action;

  const signupColumns: DataTableColumn<{ weekStart: string; churches: number }>[] = [
    {
      id: 'weekStart',
      header: t('churches.columns.created'),
      cell: (row) => formatDate(row.weekStart),
    },
    {
      id: 'churches',
      header: t('dashboard.churches'),
      align: 'right',
      cell: (row) => t('dashboard.growth.weekChurches', { count: row.churches }),
    },
  ];

  const moduleColumns: DataTableColumn<{ moduleCode: string; churches: number }>[] = [
    {
      id: 'module',
      header: t('dashboard.adoption.modules'),
      cell: (row) => moduleLabel(row.moduleCode),
    },
    {
      id: 'churches',
      header: t('dashboard.churches'),
      align: 'right',
      cell: (row) => String(row.churches),
    },
  ];

  const securityColumns: DataTableColumn<PlatformDashboard['security']['recentCritical'][number]>[] =
    [
      {
        id: 'createdAt',
        header: t('audit.columns.when'),
        cell: (row) => formatDateTime(row.createdAt),
      },
      {
        id: 'action',
        header: t('audit.columns.action'),
        cell: (row) => actionLabel(row.action),
      },
      {
        id: 'church',
        header: t('audit.columns.church'),
        cell: (row) => row.churchName ?? t('dashboard.none'),
        hideBelowMd: true,
      },
      {
        id: 'actor',
        header: t('audit.columns.actor'),
        cell: (row) => row.actorName ?? t('dashboard.none'),
        hideBelowMd: true,
      },
    ];

  return (
    <div className="space-y-8">
      <PageHeader
        title={t('dashboard.title')}
        description={t('dashboard.description')}
        meta={
          data ? (
            <p className="text-caption text-fg-secondary">
              {t('dashboard.updatedAt', { when: formatDateTime(data.generatedAt) })}
            </p>
          ) : null
        }
      />

      {dashboard.isLoading && !data ? (
        <div className="flex justify-center py-16">
          <Spinner className="size-6 text-brand" />
        </div>
      ) : !data ? (
        <p className="text-body-sm text-danger">{t('dashboard.loadFailed')}</p>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle>{t('dashboard.attention.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              <AttentionLink
                to="/churches?subscriptionStatus=past_due,grace,suspended"
                count={data.attention.overdueTotal}
              >
                {t('dashboard.attention.overdue', { count: data.attention.overdueTotal })}
              </AttentionLink>
              {data.attention.overdue.map((row) => (
                <Link
                  key={row.churchId}
                  to={`/churches?churchId=${row.churchId}`}
                  className="ml-3 block rounded-md px-3 py-1.5 text-caption text-fg-secondary hover:bg-surface-sunken hover:text-fg"
                >
                  {row.churchName}
                  {row.pastDueSince
                    ? ` · ${t('dashboard.attention.overdueSince', { when: formatDate(row.pastDueSince) })}`
                    : ''}
                </Link>
              ))}
              <AttentionLink to="/churches?subscriptionStatus=trialing" count={data.attention.trialsEndingTotal}>
                {t('dashboard.attention.trialsEnding', { count: data.attention.trialsEndingTotal })}
              </AttentionLink>
              {data.attention.trialsEnding.map((row) => (
                <Link
                  key={row.churchId}
                  to={`/churches?churchId=${row.churchId}`}
                  className="ml-3 block rounded-md px-3 py-1.5 text-caption text-fg-secondary hover:bg-surface-sunken hover:text-fg"
                >
                  {row.churchName} · {t('dashboard.attention.trialEnds', { when: formatDate(row.trialEndsAt) })}
                </Link>
              ))}
              <AttentionLink
                to="/churches"
                count={data.attention.paymentConnectionsNeedingAttention}
              >
                {t('dashboard.attention.paymentConnections', {
                  count: data.attention.paymentConnectionsNeedingAttention,
                })}
              </AttentionLink>
              <AttentionLink to="/churches" count={data.attention.giftsNeedingReview}>
                {t('dashboard.attention.giftsReview', { count: data.attention.giftsNeedingReview })}
              </AttentionLink>
              <AttentionLink
                to="/churches?paymentsSuspended=true"
                count={data.attention.paymentsSuspended}
              >
                {t('dashboard.attention.paymentsSuspended', { count: data.attention.paymentsSuspended })}
              </AttentionLink>
              <AttentionLink to="/operations?tab=deadLetters" count={data.attention.unresolvedFailedTasks}>
                {t('dashboard.attention.failedTasks', { count: data.attention.unresolvedFailedTasks })}
              </AttentionLink>
              <AttentionLink to="/operations" count={data.system.tasksOverdue.length}>
                {t('dashboard.attention.staleTasks', { count: data.system.tasksOverdue.length })}
              </AttentionLink>
              <AttentionLink
                to="/support/tickets?status=awaiting_platform"
                count={data.attention.supportTicketsAwaitingReply}
              >
                {t('dashboard.attention.supportTicketsAwaiting', {
                  count: data.attention.supportTicketsAwaitingReply,
                })}
              </AttentionLink>
              <AttentionLink
                to="/support/tickets?escalatedOnly=true"
                count={data.attention.supportTicketsEscalated}
              >
                {t('dashboard.attention.supportTicketsEscalated', {
                  count: data.attention.supportTicketsEscalated,
                })}
              </AttentionLink>
              <AttentionLink
                to="/support/tickets?slaBreached=true"
                count={data.attention.supportTicketsSlaBreached}
              >
                {t('dashboard.attention.supportTicketsOverdue', {
                  count: data.attention.supportTicketsSlaBreached,
                })}
              </AttentionLink>
              <AttentionLink to="/support-access" count={data.attention.activeSupportVisits}>
                {t('dashboard.attention.supportVisits', { count: data.attention.activeSupportVisits })}
              </AttentionLink>
            </CardContent>
          </Card>

          <section className="space-y-4">
            <div>
              <h2 className="text-card-title font-semibold text-fg">{t('dashboard.growth.title')}</h2>
              <p className="mt-1 text-body-sm text-fg-secondary">
                {t('dashboard.growth.description')}{' '}
                {t('dashboard.windowDays', { count: data.growth.windowDays })}
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard
                label={t('dashboard.churches')}
                value={data.growth.churches.total}
                hint={t('dashboard.activeChurches', { count: data.growth.churches.active })}
              />
              <StatCard
                label={t('dashboard.growth.newChurches')}
                value={data.growth.churches.newInWindow}
              />
              <StatCard
                label={t('dashboard.users')}
                value={data.growth.users.total}
                hint={t('dashboard.activeUsers', { count: data.growth.users.active })}
              />
              <StatCard label={t('dashboard.growth.newUsers')} value={data.growth.users.newInWindow} />
            </div>
            <Card>
              <CardHeader>
                <CardTitle>{t('dashboard.growth.signups')}</CardTitle>
              </CardHeader>
              <CardContent>
                <DataTable
                  rows={data.growth.signupsByWeek}
                  columns={signupColumns}
                  rowKey={(row) => row.weekStart}
                  empty={<p className="py-6 text-center text-body-sm text-fg-secondary">{t('dashboard.billing.none')}</p>}
                />
              </CardContent>
            </Card>
          </section>

          <section className="space-y-4">
            <div>
              <h2 className="text-card-title font-semibold text-fg">{t('dashboard.billing.title')}</h2>
              <p className="mt-1 text-body-sm text-fg-secondary">{t('dashboard.billing.description')}</p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {SUBSCRIPTION_STATUSES.map((status) => (
                <Link key={status} to={`/churches?subscriptionStatus=${status}`} className="block">
                  <StatCard
                    label={t(`billing.statuses.${status}`)}
                    value={data.subscriptions.byStatus[status]}
                  />
                </Link>
              ))}
            </div>
            <div className="grid gap-4 lg:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>{t('dashboard.billing.byPlan')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {data.subscriptions.byPlan.length === 0 ? (
                    <p className="text-body-sm text-fg-secondary">{t('dashboard.billing.none')}</p>
                  ) : (
                    data.subscriptions.byPlan.map((row) => (
                      <div key={row.plan} className="flex items-center justify-between gap-2 text-body-sm">
                        <span>{t(`billing.plans.${row.plan as 'essential'}`)}</span>
                        <span className="tabular-nums font-medium">{row.churches}</span>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>{t('dashboard.billing.collected')}</CardTitle>
                  <CardDescription>{t('dashboard.windowDays', { count: data.subscriptions.windowDays })}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {data.subscriptions.collected.length === 0 ? (
                    <p className="text-body-sm text-fg-secondary">{t('dashboard.billing.none')}</p>
                  ) : (
                    data.subscriptions.collected.map((row) => (
                      <div key={row.currency} className="flex items-center justify-between gap-2 text-body-sm">
                        <span className="tabular-nums font-medium">
                          {formatMoney(row.amountMinor, row.currency)}
                        </span>
                        <span className="text-fg-secondary">
                          {t('dashboard.billing.payments', { count: row.payments })}
                        </span>
                      </div>
                    ))
                  )}
                  <p className="pt-2 text-body-sm font-medium">{t('dashboard.billing.outstanding')}</p>
                  {data.subscriptions.outstanding.length === 0 ? (
                    <p className="text-body-sm text-fg-secondary">{t('dashboard.billing.none')}</p>
                  ) : (
                    data.subscriptions.outstanding.map((row) => (
                      <div key={row.currency} className="flex items-center justify-between gap-2 text-body-sm">
                        <span className="tabular-nums font-medium">
                          {formatMoney(row.amountMinor, row.currency)}
                        </span>
                        <span className="text-fg-secondary">
                          {t('dashboard.billing.invoices', { count: row.invoices })}
                        </span>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
            </div>
          </section>

          <section className="space-y-4">
            <div>
              <h2 className="text-card-title font-semibold text-fg">{t('dashboard.adoption.title')}</h2>
              <p className="mt-1 text-body-sm text-fg-secondary">{t('dashboard.adoption.description')}</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <StatCard label={t('dashboard.adoption.consoleUsers')} value={data.adoption.consoleUsers} />
              <StatCard label={t('dashboard.adoption.appMembers')} value={data.adoption.appMembers} />
              <StatCard
                label={t('dashboard.adoption.onlineGiving')}
                value={data.adoption.onlineGivingConnected}
              />
            </div>
            <Card>
              <CardHeader>
                <CardTitle>{t('dashboard.adoption.modules')}</CardTitle>
              </CardHeader>
              <CardContent>
                <DataTable
                  rows={data.adoption.modules}
                  columns={moduleColumns}
                  rowKey={(row) => row.moduleCode}
                  empty={<p className="py-6 text-center text-body-sm text-fg-secondary">{t('dashboard.billing.none')}</p>}
                />
              </CardContent>
            </Card>
          </section>

          <section className="space-y-4">
            <div>
              <h2 className="text-card-title font-semibold text-fg">{t('dashboard.giving.title')}</h2>
              <p className="mt-1 text-body-sm text-fg-secondary">
                {t('dashboard.giving.description')} {t('dashboard.windowDays', { count: data.giving.windowDays })}
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <StatCard label={t('dashboard.giving.attempts')} value={data.giving.attempts} />
              <StatCard label={t('dashboard.giving.succeeded')} value={data.giving.succeeded} />
              <StatCard label={t('dashboard.giving.failed')} value={data.giving.failed} />
              <StatCard label={t('dashboard.giving.waiting')} value={data.giving.waiting} />
              <StatCard label={t('dashboard.giving.needsReview')} value={data.giving.needsReview} />
              <StatCard
                label={t('dashboard.giving.successRate')}
                value={formatRate(data.giving.successRate, locale)}
              />
            </div>
          </section>

          <section className="space-y-4">
            <div>
              <h2 className="text-card-title font-semibold text-fg">{t('dashboard.messaging.title')}</h2>
              <p className="mt-1 text-body-sm text-fg-secondary">
                {t('dashboard.messaging.description')}{' '}
                {t('dashboard.windowDays', { count: data.messaging.windowDays })}
              </p>
            </div>
            <div className="grid gap-4 lg:grid-cols-4">
              {data.messaging.byChannel.map((row) => (
                <Card key={row.channel}>
                  <CardHeader>
                    <CardTitle>{t(`dashboard.messaging.channels.${row.channel}`)}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-1 text-body-sm">
                    <p>
                      {t('dashboard.messaging.total')}: <span className="tabular-nums font-medium">{row.total}</span>
                    </p>
                    <p>
                      {t('dashboard.messaging.delivered')}:{' '}
                      <span className="tabular-nums font-medium">{row.delivered}</span>
                    </p>
                    <p>
                      {t('dashboard.messaging.failed')}:{' '}
                      <span className="tabular-nums font-medium">{row.failed}</span>
                    </p>
                  </CardContent>
                </Card>
              ))}
              <StatCard
                label={t('dashboard.messaging.failureRate')}
                value={formatRate(data.messaging.failureRate, locale)}
              />
            </div>
          </section>

          <section className="space-y-4">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <h2 className="text-card-title font-semibold text-fg">{t('dashboard.security.title')}</h2>
                <p className="mt-1 text-body-sm text-fg-secondary">{t('dashboard.security.description')}</p>
              </div>
              <Link to="/audit?severity=critical" className="text-body-sm font-medium text-brand hover:underline">
                {t('dashboard.security.viewActivity')}
              </Link>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <StatCard
                label={t('dashboard.security.withoutTwoStep')}
                value={data.security.platformStaffWithoutTwoStep}
              />
              <Link to="/users" className="block">
                <StatCard
                  label={t('dashboard.security.blockedUsers')}
                  value={data.security.suspendedOrBannedUsers}
                />
              </Link>
              <StatCard
                label={t('dashboard.security.stolenSignIns')}
                value={data.security.stolenSignInsBlocked}
              />
            </div>
            <Card>
              <CardHeader>
                <CardTitle>{t('dashboard.security.recentCritical')}</CardTitle>
              </CardHeader>
              <CardContent>
                <DataTable
                  rows={data.security.recentCritical}
                  columns={securityColumns}
                  rowKey={(row) => row.id}
                  empty={
                    <p className="py-6 text-center text-body-sm text-fg-secondary">
                      {t('dashboard.security.recentEmpty')}
                    </p>
                  }
                />
              </CardContent>
            </Card>
          </section>

          <section className="space-y-4">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <h2 className="text-card-title font-semibold text-fg">{t('dashboard.system.title')}</h2>
                <p className="mt-1 text-body-sm text-fg-secondary">{t('dashboard.system.description')}</p>
              </div>
              <Link to="/operations" className="text-body-sm font-medium text-brand hover:underline">
                {t('dashboard.system.openJobs')}
              </Link>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard
                label={t('dashboard.system.database')}
                value={
                  data.system.database === 'ok'
                    ? t('dashboard.system.databaseOk')
                    : t('dashboard.system.databaseUnavailable')
                }
              />
              <StatCard
                label={t('dashboard.system.scheduler')}
                value={
                  data.system.schedulerEnabled
                    ? t('dashboard.system.schedulerOn')
                    : t('dashboard.system.schedulerOff')
                }
              />
              <StatCard
                label={t('dashboard.system.email')}
                value={
                  data.system.email.configured
                    ? t('dashboard.system.configured')
                    : t('dashboard.system.notConfigured')
                }
              />
              <StatCard
                label={t('dashboard.system.sms')}
                value={
                  data.system.sms.configured
                    ? t('dashboard.system.configured')
                    : t('dashboard.system.notConfigured')
                }
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <StatCard
                label={t('dashboard.system.uptime')}
                value={formatUptime(data.system.uptimeSeconds, t)}
              />
              <Card>
                <CardHeader>
                  <CardTitle>{t('dashboard.system.failingTasks')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-body-sm">
                  {data.system.tasksFailingNow.length === 0 ? (
                    <p className="text-fg-secondary">{t('dashboard.system.noneFailing')}</p>
                  ) : (
                    data.system.tasksFailingNow.map((name) => <p key={name}>{jobLabel(name)}</p>)
                  )}
                  <p className="pt-2 font-medium">{t('dashboard.system.overdueTasks')}</p>
                  {data.system.tasksOverdue.length === 0 ? (
                    <p className="text-fg-secondary">{t('dashboard.system.noneFailing')}</p>
                  ) : (
                    data.system.tasksOverdue.map((name) => <p key={name}>{jobLabel(name)}</p>)
                  )}
                </CardContent>
              </Card>
            </div>
          </section>
        </>
      )}
    </div>
  );
};
